import { 
  ref, 
  push, 
  onValue, 
  serverTimestamp,
  query,
  orderByChild,
  limitToLast,
  set,
  get
} from 'firebase/database';
import { database } from '../config/firebase';

class ChatService {
  constructor() {
    this.messagesRef = ref(database, 'messages');
    this.usersRef = ref(database, 'users');
    this.statsRef = ref(database, 'stats');
    this.notificationsRef = ref(database, 'notifications');
    this.ratingsRef = ref(database, 'ratings');
    this.listeners = new Map();
    this.openChats = new Set(); // Отслеживаем открытые чаты
    this.userId = this.getUserId();
  }

  // Отправка сообщения
  async sendMessage(text, userInfo = {}) {
    try {
      const messageData = {
        text: text.trim(),
        timestamp: serverTimestamp(),
        userInfo: {
          avatar: userInfo.avatar || '👤',
          color: userInfo.color || '#3B82F6',
          ...userInfo
        },
        id: Date.now() + Math.random() // Уникальный ID
      };

      await push(this.messagesRef, messageData);
      return true;
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      return false;
    }
  }

  // Подписка на новые сообщения
  subscribeToMessages(callback) {
    const messagesQuery = query(
      this.messagesRef,
      orderByChild('timestamp'),
      limitToLast(50) // Последние 50 сообщений
    );

    const unsubscribe = onValue(messagesQuery, (snapshot) => {
      const messages = [];
      snapshot.forEach((childSnapshot) => {
        const message = childSnapshot.val();
        if (message && message.text) {
          messages.push({
            id: message.id || childSnapshot.key,
            text: message.text,
            timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
            userInfo: message.userInfo || { avatar: '👤', color: '#3B82F6' },
            sender: 'user'
          });
        }
      });
      
      // Сортируем по времени
      messages.sort((a, b) => a.timestamp - b.timestamp);
      callback(messages);
    });

    this.listeners.set('messages', unsubscribe);
    return unsubscribe;
  }

  // Подписка на количество пользователей онлайн
  subscribeToUserCount(callback) {
    const unsubscribe = onValue(this.usersRef, (snapshot) => {
      const users = snapshot.val() || {};
      const now = Date.now();
      const onlineUsers = Object.keys(users).filter(key => {
        const user = users[key];
        const lastSeen = user.lastSeen || 0;
        const timeDiff = now - lastSeen;
        return timeDiff < 30000; // Онлайн если активен последние 30 секунд
      });
      
      callback(onlineUsers.length);
    });

    this.listeners.set('userCount', unsubscribe);
    return unsubscribe;
  }

  // Отметить пользователя как онлайн
  async markUserOnline() {
    try {
      const userRef = ref(database, `users/${this.userId}`);
      await set(userRef, {
        lastSeen: serverTimestamp(),
        online: true,
        joinedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Ошибка обновления статуса пользователя:', error);
    }
  }

  // Отметить пользователя как оффлайн
  async markUserOffline() {
    try {
      const userRef = ref(database, `users/${this.userId}`);
      await set(userRef, {
        lastSeen: serverTimestamp(),
        online: false
      });
    } catch (error) {
      console.error('Ошибка обновления статуса пользователя:', error);
    }
  }

  // Увеличить счетчик замененных ключей
  async incrementKeyReplacements() {
    try {
      const statsRef = ref(database, 'stats/keyReplacements');
      const snapshot = await get(statsRef);
      const currentCount = snapshot.val() || 0;
      await set(statsRef, currentCount + 1);
      
      // Также обновляем статистику по дням
      const today = new Date().toISOString().split('T')[0];
      const dailyRef = ref(database, `stats/daily/${today}`);
      const dailySnapshot = await get(dailyRef);
      const dailyCount = dailySnapshot.val() || 0;
      await set(dailyRef, dailyCount + 1);
    } catch (error) {
      console.error('Ошибка обновления статистики:', error);
    }
  }

  // Отправить глобальное уведомление об успешной замене
  async sendGlobalSuccessNotification(orderNumber) {
    try {
      const notification = {
        type: 'success',
        orderNumber: orderNumber,
        message: `Заказ #${orderNumber} успешно восстановлен!`,
        timestamp: serverTimestamp(),
        expiresAt: Date.now() + 10000 // Истекает через 10 секунд
      };
      
      await push(this.notificationsRef, notification);
      
      // Очищаем старые уведомления (старше 30 секунд)
      setTimeout(() => {
        this.cleanupOldNotifications();
      }, 1000);
    } catch (error) {
      console.warn('Ошибка отправки глобального уведомления (не критично):', error.message);
      // Не критично, продолжаем работу без уведомлений
    }
  }

  // Подписка на глобальные уведомления
  subscribeToGlobalNotifications(callback) {
    try {
      // Пробуем использовать индексированный запрос
      const notificationsQuery = query(this.notificationsRef, orderByChild('timestamp'), limitToLast(10));

      const unsubscribe = onValue(notificationsQuery, (snapshot) => {
        const notifications = [];
        const now = Date.now();
        const shownNotifications = this.getShownNotifications();
        
        snapshot.forEach((childSnapshot) => {
          const notification = childSnapshot.val();
          const notificationId = childSnapshot.key;
          
          // Показываем только свежие уведомления (не старше 30 секунд) и не показанные ранее
          if (notification.timestamp && 
              (now - notification.timestamp) < 30000 && 
              !shownNotifications.includes(notificationId)) {
            notifications.push({
              id: notificationId,
              ...notification,
              timestamp: notification.timestamp ? new Date(notification.timestamp) : new Date()
            });
          }
        });
        
        // Сортируем по времени (новые сверху)
        notifications.sort((a, b) => b.timestamp - a.timestamp);
        callback(notifications);
      }, (error) => {
        console.warn('Firebase connection error, using fallback mode:', error);
        // Fallback: просто возвращаем пустой массив
        callback([]);
      });

      this.listeners.set('notifications', unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.warn('Firebase setup error, notifications disabled:', error);
      // Возвращаем заглушку
      return () => {};
    }
  }

  // Получить список показанных уведомлений из localStorage
  getShownNotifications() {
    try {
      const shown = localStorage.getItem('shownNotifications');
      return shown ? JSON.parse(shown) : [];
    } catch (error) {
      return [];
    }
  }

  // Отметить уведомление как показанное
  markNotificationAsShown(notificationId) {
    try {
      const shown = this.getShownNotifications();
      if (!shown.includes(notificationId)) {
        shown.push(notificationId);
        // Храним только последние 50 ID
        if (shown.length > 50) {
          shown.splice(0, shown.length - 50);
        }
        localStorage.setItem('shownNotifications', JSON.stringify(shown));
      }
    } catch (error) {
      console.error('Ошибка сохранения показанного уведомления:', error);
    }
  }

  // Записать статистику показа уведомления в Firebase
  async recordNotificationView(notificationId, orderNumber) {
    try {
      const viewData = {
        notificationId,
        orderNumber,
        userId: this.userId,
        viewedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      };
      
      await push(ref(database, 'notificationViews'), viewData);
    } catch (error) {
      console.error('Ошибка записи статистики просмотра:', error);
    }
  }

  // Очистка старых уведомлений (упрощенная версия без индексов)
  async cleanupOldNotifications() {
    try {
      // Просто получаем все уведомления и фильтруем на клиенте
      const snapshot = await get(this.notificationsRef);
      if (!snapshot.exists()) return;
      
      const now = Date.now();
      const updates = {};
      
      snapshot.forEach((childSnapshot) => {
        const notification = childSnapshot.val();
        // Удаляем уведомления старше 30 секунд
        if (notification.timestamp && (now - notification.timestamp) > 30000) {
          updates[childSnapshot.key] = null;
        }
      });
      
      if (Object.keys(updates).length > 0) {
        // Используем update вместо set для частичного обновления
        const notificationRef = ref(database, 'notifications');
        await set(notificationRef, updates);
      }
    } catch (error) {
      console.error('Ошибка очистки старых уведомлений:', error);
      // Не критично, продолжаем работу
    }
  }

  // Получить статистику замененных ключей
  async getKeyReplacementsStats() {
    try {
      const statsRef = ref(database, 'stats/keyReplacements');
      const snapshot = await get(statsRef);
      return snapshot.val() || 0;
    } catch (error) {
      console.error('Ошибка получения статистики:', error);
      return 0;
    }
  }

  // Подписка на статистику замененных ключей
  subscribeToKeyReplacementsStats(callback) {
    const unsubscribe = onValue(this.statsRef, (snapshot) => {
      const stats = snapshot.val() || {};
      callback({
        total: stats.keyReplacements || 0,
        daily: stats.daily || {}
      });
    });

    this.listeners.set('keyStats', unsubscribe);
    return unsubscribe;
  }

  // Обновление статуса пользователя
  updateUserStatus(userInfo = {}) {
    // Отмечаем пользователя как онлайн
    this.markUserOnline();
    
    // Обновляем статус каждые 10 секунд
    this.statusInterval = setInterval(() => {
      this.markUserOnline();
    }, 10000);
  }

  // Остановить обновление статуса
  stopStatusUpdates() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
    }
    this.markUserOffline();
  }

  // Получение уникального ID пользователя
  getUserId() {
    let userId = localStorage.getItem('chatUserId');
    if (!userId) {
      userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chatUserId', userId);
    }
    return userId;
  }

  // Отключение от всех подписок
  unsubscribeAll() {
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();
    this.stopStatusUpdates();
  }

  // Очистка старых сообщений (оставляем последние 100)
  async cleanupOldMessages() {
    try {
      // Здесь можно добавить логику очистки старых сообщений
      // Для простоты пока не реализуем
      console.log('Очистка сообщений не реализована');
    } catch (error) {
      console.error('Ошибка очистки сообщений:', error);
    }
  }

  // ============ EMAIL SUBSCRIPTIONS ============

  // Сохранение email и подписки
  async saveEmailSubscription(orderNumber, email, subscriptionUrl) {
    try {
      const emailRef = ref(database, 'email_subscriptions');
      const subscriptionData = {
        orderNumber: orderNumber,
        email: email,
        subscription: subscriptionUrl,
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0],
        sent: false // Флаг для бэкенда - отправлено ли письмо
      };

      await push(emailRef, subscriptionData);
      console.log('Email subscription saved to Firebase');
      return true;
    } catch (error) {
      console.error('Ошибка сохранения email подписки:', error);
      return false;
    }
  }

  // ============ РЕЙТИНГИ ============

  // Отправка рейтинга
  async submitRating(rating, feedback, clientIP) {
    try {
      const ratingData = {
        rating: parseInt(rating),
        feedback: feedback.trim(),
        clientIP: clientIP,
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0]
      };

      await push(this.ratingsRef, ratingData);

      // Обновляем общую статистику рейтингов
      await this.updateRatingStats(rating);

      return true;
    } catch (error) {
      console.error('Ошибка отправки рейтинга:', error);
      return false;
    }
  }

  // Проверка, голосовал ли пользователь с этого IP
  async hasUserRated(clientIP) {
    try {
      const snapshot = await get(this.ratingsRef);
      if (snapshot.exists()) {
        const ratings = snapshot.val();
        return Object.values(ratings).some(r => r.clientIP === clientIP);
      }
      return false;
    } catch (error) {
      console.error('Ошибка проверки рейтинга:', error);
      return false;
    }
  }

  // Обновление статистики рейтингов
  async updateRatingStats(rating) {
    try {
      const ratingStatsRef = ref(database, 'stats/ratings');
      const snapshot = await get(ratingStatsRef);
      
      const currentStats = snapshot.exists() ? snapshot.val() : {
        total: 0,
        count: 0,
        distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      };

      const newTotal = currentStats.total + rating;
      const newCount = currentStats.count + 1;
      const newDistribution = { ...currentStats.distribution };
      newDistribution[rating] = (newDistribution[rating] || 0) + 1;

      await set(ratingStatsRef, {
        total: newTotal,
        count: newCount,
        average: (newTotal / newCount).toFixed(2),
        distribution: newDistribution,
        lastUpdated: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Ошибка обновления статистики рейтингов:', error);
      return false;
    }
  }

  // Подписка на статистику рейтингов
  subscribeToRatingStats(callback) {
    const ratingStatsRef = ref(database, 'stats/ratings');
    
    const unsubscribe = onValue(ratingStatsRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val());
      } else {
        callback({
          total: 0,
          count: 0,
          average: '0.00',
          distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        });
      }
    });

    this.listeners.set('ratingStats', unsubscribe);
    return unsubscribe;
  }

  // Получение всех рейтингов
  async getAllRatings() {
    try {
      const snapshot = await get(this.ratingsRef);
      if (snapshot.exists()) {
        const ratings = [];
        snapshot.forEach((childSnapshot) => {
          ratings.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        return ratings.sort((a, b) => b.timestamp - a.timestamp);
      }
      return [];
    } catch (error) {
      console.error('Ошибка получения рейтингов:', error);
      return [];
    }
  }
}

const chatService = new ChatService();
export default chatService;
